import React, { useEffect, useMemo } from "react";
import { Box, Card, CardContent } from "@mui/material";
import Map from "../../Map";
import ProjectBoundingBoxLayer from "../../layer_components/ProjectBoundingBoxLayer";
import CreateProjectTileSelector from "../../layer_components/CreateProjectTileSelector";
import { SelectionProvider, useSelection } from "../../../contexts/SelectionContext";
import { MapProvider, useMap } from "../../../contexts/MapContext";
import { StepProps } from "../types";
import { useProjects } from "../../../api/projects";
import { bboxFromBoundary } from "../../../utils/boundaryUtils";
import { useProjectAreaPreview } from "../useProjectAreaPreview";
import LocationSizeControls from "./LocationSizeControls";

const LocationSizeStepContent: React.FC<StepProps> = (props) => {
  const { onUpdateData } = props;
  const { selectedLatLng } = useSelection();
  const { projects } = useProjects();
  const { map } = useMap();
  // Pin, tiles and property previews: shared with the listing page's
  // new-project flow, which draws them on the listing map instead.
  const area = useProjectAreaPreview(props.data, onUpdateData);

  const initialMapCenter = useMemo(() => {
    const withBoundary = (projects?.data ?? []).filter((p) => p.boundary);
    if (!withBoundary.length) return { lat: -25, lng: 134 }; // centre of Australia
    const centers = withBoundary
      .map((p) => { const b = bboxFromBoundary(p.boundary); return b ? { lat: (b.top + b.bottom) / 2, lng: (b.left + b.right) / 2 } : null; })
      .filter(Boolean) as { lat: number; lng: number }[];
    if (!centers.length) return undefined;
    const sortedLats = [...centers.map((c) => c.lat)].sort((a, b) => a - b);
    const sortedLngs = [...centers.map((c) => c.lng)].sort((a, b) => a - b);
    const mid = Math.floor(sortedLats.length / 2);
    return { lat: sortedLats[mid], lng: sortedLngs[mid] };
  }, [projects?.data]);

  // Update selectedLatLng in data when it changes. Picking a new location
  // moves the grid origin, so any previously-expanded tiles are discarded.
  useEffect(() => {
    if (selectedLatLng) {
      onUpdateData({ selectedLatLng, expansionTiles: [] });
    }
  }, [selectedLatLng]); // Remove onUpdateData from dependencies

  // Other projects layers
  const otherProjectLayers = projects?.data?.filter(project => !!project.boundary)?.map((project) =>
    ProjectBoundingBoxLayer(
      `other-project-${project.id}`,
      `Other Project: ${project.name}`,
      project.boundary
    )
  ) || [];


  return (
    <Box sx={{ display: "flex", height: "100%", bgcolor: "#f5f5f5" }}>
      {/* Left Panel - Controls */}
      <Box sx={{ 
        width: "35%",
        p: 3, 
        backgroundColor: "#f8f9fa",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflow: "auto"
      }}>
        <LocationSizeControls {...props} area={area} />
        {/* Global navigation bar handles Previous/Next */}
      </Box>

      {/* Right Panel - Map */}
      <Box sx={{ width: "65%", p: 3 }}>
        <Card sx={{ height: "100%", maxHeight: "calc(var(--vh100) - 200px)" }}>
          <CardContent sx={{ height: "100%", p: 0, overflow: "hidden" }}>
            <Map
              layers={[...area.layers, ...otherProjectLayers]}
              searchable={true}
              latlng={initialMapCenter}
              initialZoom={4}
            />
            {area.canExpand && area.tileGrid && (
              <CreateProjectTileSelector
                map={map}
                originLng={area.tileGrid.originLng}
                originLat={area.tileGrid.originLat}
                tileWidthDeg={area.tileGrid.tileWidthDeg}
                tileHeightDeg={area.tileGrid.tileHeightDeg}
                tiles={area.selectedTiles}
                onAdd={area.addTile}
                onRemove={area.removeTile}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const LocationSizeStep: React.FC<StepProps> = (props) => {
  return (
    // The step unmounts when the wizard moves on, taking the selection context
    // with it, so seed it from the wizard's own copy of the location. That
    // restores the pin and bounding box when the user steps back, and shows
    // them immediately for a location picked on the project listing map.
    <SelectionProvider initialLatLng={props.data.selectedLatLng}>
      <MapProvider>
        <LocationSizeStepContent {...props} />
      </MapProvider>
    </SelectionProvider>
  );
};

export default LocationSizeStep;
